import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";

// Default brand colors - Using invent.ai purple as fallback
const DEFAULT_CHART_COLORS = ["#e066ff", "#0F172A", "#0EA5E9", "#64748B", "#10B981"];

const DEFAULT_BRAND_SETTINGS = {
  primary_color: "#e066ff",
  secondary_color: "#0F172A",
  accent_color: "#0EA5E9",
  chart_colors: DEFAULT_CHART_COLORS,
  logo_url: null,
  logo_base64: null
};

const BrandingContext = createContext(null);

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    return {
      brandSettings: DEFAULT_BRAND_SETTINGS,
      chartColors: DEFAULT_CHART_COLORS,
      pageTitles: {},
      platformName: null,
      loading: false,
      refreshBranding: () => {}
    };
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const [brandSettings, setBrandSettings] = useState(DEFAULT_BRAND_SETTINGS);
  const [pageTitles, setPageTitles] = useState({});
  const [platformName, setPlatformName] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchAttempts = useRef(0);
  const maxAttempts = 10;
  const hasFetchedSuccessfully = useRef(false);

  // Simple fetch function - calls /api/tenants/my-branding which figures out tenant from user
  // CRITICAL: Also passes X-Tenant-ID when super admin is viewing a specific tenant
  const fetchBranding = useCallback(async () => {
    const sessionToken = localStorage.getItem("session_token");
    if (!sessionToken) {
      console.log("BrandingContext: No session token");
      setLoading(false);
      return;
    }

    try {
      // Get viewing_tenant_id from sessionStorage (set when super admin enters a tenant)
      const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
      console.log("BrandingContext: Fetching from /api/tenants/my-branding...", "viewingTenantId:", viewingTenantId);
      
      const params = { Authorization: `Bearer ${sessionToken}`, _t: Date.now() };
      // CRITICAL: Pass X-Tenant-ID so backend returns branding for viewed tenant
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      
      const response = await axios.get(`${API}/tenants/my-branding`, { params });

      console.log("BrandingContext: Response:", response.data);

      if (response.data && response.data.chart_colors) {
        // Check settings_version - if server version is newer, we need to update
        const serverVersion = response.data.settings_version || 0;
        const cachedVersion = parseInt(localStorage.getItem("settings_version") || "0", 10);
        
        if (serverVersion > cachedVersion) {
          console.log(`BrandingContext: Settings version changed (${cachedVersion} -> ${serverVersion}), forcing refresh`);
          // Clear any cached branding data
          localStorage.setItem("settings_version", serverVersion.toString());
        }
        
        const newSettings = {
          primary_color: response.data.primary_color,
          secondary_color: response.data.secondary_color,
          accent_color: response.data.accent_color || response.data.primary_color,
          chart_colors: response.data.chart_colors,
          logo_url: response.data.logo_url || null,
          logo_base64: null
        };
        console.log("BrandingContext: SUCCESS - Setting colors:", response.data.chart_colors);
        setBrandSettings(newSettings);
        
        // Always update page titles and platform name from server (source of truth)
        console.log("BrandingContext: Setting page titles:", response.data.page_titles);
        setPageTitles(response.data.page_titles || {});
        
        console.log("BrandingContext: Setting platform name:", response.data.platform_name);
        setPlatformName(response.data.platform_name || null);
        
        hasFetchedSuccessfully.current = true;
        fetchAttempts.current = 0;
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error("BrandingContext: Error fetching branding:", error.message);
    }

    // Retry if we haven't succeeded yet
    if (!hasFetchedSuccessfully.current && fetchAttempts.current < maxAttempts) {
      fetchAttempts.current++;
      console.log(`BrandingContext: Attempt ${fetchAttempts.current}/${maxAttempts} failed, retrying in 500ms...`);
      setTimeout(() => fetchBranding(), 500);
    } else {
      console.log("BrandingContext: Max attempts reached or already succeeded");
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    console.log("BrandingContext: Mounted, starting fetch...");
    fetchBranding();
  }, [fetchBranding]);

  // Listen for tenant changes (when super admin switches tenants)
  useEffect(() => {
    const handleTenantChange = () => {
      console.log("BrandingContext: tenant-changed event, refreshing...");
      hasFetchedSuccessfully.current = false;
      fetchAttempts.current = 0;
      fetchBranding();
    };

    window.addEventListener("tenant-changed", handleTenantChange);
    return () => window.removeEventListener("tenant-changed", handleTenantChange);
  }, [fetchBranding]);

  // Listen for auth changes
  useEffect(() => {
    const handleAuthChange = () => {
      console.log("BrandingContext: auth-changed event, refreshing...");
      hasFetchedSuccessfully.current = false;
      fetchAttempts.current = 0;
      fetchBranding();
    };

    window.addEventListener("auth-changed", handleAuthChange);
    return () => window.removeEventListener("auth-changed", handleAuthChange);
  }, [fetchBranding]);

  const value = {
    brandSettings,
    chartColors: brandSettings.chart_colors,
    pageTitles,
    platformName,
    loading,
    refreshBranding: () => {
      hasFetchedSuccessfully.current = false;
      fetchAttempts.current = 0;
      fetchBranding();
    }
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
